import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { KeyRound, ArrowRight, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [logoutReason, setLogoutReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotResult, setForgotResult] = useState(null);

  // عرض سبب تسجيل الخروج إذا وجد
  React.useEffect(() => {
    const reason = sessionStorage.getItem('logout_reason');
    if (reason) {
      setLogoutReason(reason);
      sessionStorage.removeItem('logout_reason');
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    // 🛡️ Client-side safety timeout: if the server is unresponsive for 20s,
    // stop showing "Signing in..." forever and give the user a clear retry
    // hint instead. Prevents the login page from feeling "stuck".
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      setLoading(false);
      setError('الخادم بطيء في الاستجابة الآن. حاول مرّة أخرى بعد قليل.');
    }, 20000);
    try {
      const result = await login(formData.email, formData.password);
      if (timedOut) return;               // already surfaced a message
      clearTimeout(timeoutId);
      if (result.success) {
        navigate('/');
      } else {
        setError(result.error);
      }
    } catch (err) {
      if (!timedOut) {
        clearTimeout(timeoutId);
        setError(err?.message || 'حدث خطأ غير متوقّع. حاول مجدّداً.');
      }
    } finally {
      if (!timedOut) setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    setForgotResult(null);
    try {
      const res = await api.post('/auth/forgot-password', { email: forgotEmail.trim() });
      setForgotResult({ success: true, message: res.data.message });
    } catch (err) {
      setForgotResult({ success: false, message: err.response?.data?.detail || 'حدث خطأ' });
    }
    setForgotLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4" dir="rtl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden border-2 border-[#00E5FF]/30">
            <img
              src="https://customer-assets.emergentagent.com/fe0d89dd-7220-47e5-8912-618386cce67e/DALL%C2%B7E%202025-01-28%2017.56.37%20-%20A%20modern,%20professional%20logo%20representing%20a%20trading%20signals%20platform.%20The%20logo%20features%20the%20Arabic%20word%20'%D8%A7%D9%84%D8%B1%D8%A4%D9%8A%D9%87'%20(Al-Ruya)%20in%20a%20sleek,%20stylized%20fon.webp"
              alt="ALROYAH"
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="text-2xl font-bold text-white">الرؤية للتداول</h1>
          <p className="text-zinc-400 mt-2">تسجيل الدخول إلى حسابك</p>
        </div>

        {/* Form */}
        <div className="glass-panel p-6 rounded-lg">
          <AnimatePresence mode="wait">
            {!showForgot ? (
              <motion.div key="login" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {logoutReason && !error && (
                    <div className="bg-amber-500/10 border border-amber-500/30 text-amber-400 p-3 rounded-lg text-sm text-center" data-testid="logout-reason">
                      {logoutReason}
                    </div>
                  )}
                  {error && (
                    <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-sm text-center" data-testid="login-error">
                      {error}
                    </div>
                  )}
                  <div>
                    <label className="block text-zinc-400 text-sm mb-2">البريد الإلكتروني</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      autoComplete="off"
                      className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#00E5FF] focus:outline-none transition-colors"
                      placeholder="example@email.com"
                      dir="ltr"
                      data-testid="login-email"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-400 text-sm mb-2">كلمة المرور</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      autoComplete="new-password"
                      className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#00E5FF] focus:outline-none transition-colors"
                      placeholder="••••••••"
                      dir="ltr"
                      data-testid="login-password"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#00E5FF] hover:bg-[#00E5FF]/80 text-black font-bold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="login-submit"
                  >
                    {loading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
                  </button>
                </form>

                <button
                  onClick={() => { setShowForgot(true); setForgotEmail(formData.email); setForgotResult(null); }}
                  data-testid="forgot-password-btn"
                  className="w-full mt-3 text-zinc-500 hover:text-[#00E5FF] text-sm transition-colors flex items-center justify-center gap-1.5"
                >
                  <KeyRound className="w-3.5 h-3.5" />
                  نسيت كلمة المرور؟
                </button>
              </motion.div>
            ) : (
              <motion.div key="forgot" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="flex items-center gap-2 mb-4">
                  <button
                    onClick={() => { setShowForgot(false); setForgotResult(null); }}
                    className="text-zinc-500 hover:text-white transition-colors"
                  >
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  <h2 className="text-white font-bold flex items-center gap-2">
                    <KeyRound className="w-5 h-5 text-[#00E5FF]" />
                    نسيت كلمة المرور
                  </h2>
                </div>

                {forgotResult?.success ? (
                  <div className="text-center py-6">
                    <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                    <p className="text-green-400 font-medium mb-1">تم إرسال الطلب</p>
                    <p className="text-zinc-400 text-sm">{forgotResult.message}</p>
                    <button
                      onClick={() => { setShowForgot(false); setForgotResult(null); }}
                      className="mt-4 px-6 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors text-sm"
                    >
                      العودة لتسجيل الدخول
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <p className="text-zinc-400 text-sm">
                      أدخل بريدك الإلكتروني وسيتم إرسال طلب للمدير لإعادة تعيين كلمة المرور
                    </p>

                    {forgotResult && !forgotResult.success && (
                      <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-sm text-center">
                        {forgotResult.message}
                      </div>
                    )}

                    <div>
                      <label className="block text-zinc-400 text-sm mb-2">البريد الإلكتروني</label>
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                        className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#00E5FF] focus:outline-none transition-colors"
                        placeholder="example@email.com"
                        dir="ltr"
                        data-testid="forgot-email-input"
                        autoFocus
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={forgotLoading}
                      data-testid="forgot-submit-btn"
                      className="w-full bg-[#00E5FF] hover:bg-[#00E5FF]/80 text-black font-bold py-3 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {forgotLoading ? 'جاري الإرسال...' : 'إرسال طلب إعادة التعيين'}
                    </button>
                  </form>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-6 text-center">
            <p className="text-zinc-400">
              ليس لديك حساب؟{' '}
              <Link to="/register" className="text-[#00E5FF] hover:underline">
                إنشاء حساب جديد
              </Link>
            </p>
          </div>
          
          <div className="mt-4 text-center">
            <Link to="/terms" className="text-zinc-500 text-xs hover:text-[#00E5FF] transition-colors">
              الشروط والأحكام وإخلاء المسؤولية
            </Link>
          </div>
        </div>

        <p className="text-center text-zinc-500 text-xs mt-6">
          © 2025 الرؤية للتداول. جميع الحقوق محفوظة.
          <br />
          <a href="mailto:support@alroyah.com" className="text-zinc-500 hover:text-[#00E5FF] transition-colors">
            support@alroyah.com
          </a>
        </p>
      </motion.div>
    </div>
  );
};

export default Login;
